import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { getProduct } from "../../../../../http/api/ProductAPI";
import {
  getOrigin,
  getRank,
  getProductClass,
  getPacking,
  getWeightUnit,
} from "../../../../../http/api/MasterAPI";
import { getDisplaySize } from "../../../../../utils/Items";
import { DND_ITEM_TYPES } from "../supplyTableRow";
import CombinedSidebarHeader from "./CombinedSidebarHeader";
import CombinedSidebarRow from "./CombinedSidebarRow";
import FreeProduct from "./FreeProduct";
import FreeProductDragPreview from "./FreeProductDragPreview";

const TABS_LABEL = { 1: "蔬菜", 2: "果物", 3: "スポット" };
const PAGE_SIZE = 30;

const isEmpty = (v) => v == null || v === 0 || v === "" || v === "0";
const softEq = (a, b) => {
  if (isEmpty(a) && isEmpty(b)) return true;  // 両方空 → 一致
  if (isEmpty(a) || isEmpty(b)) return false; // 片方だけ空 → 不一致
  // eslint-disable-next-line eqeqeq
  return a == b;
};

const defaultPredictAdded = (product, addedProduct) =>
  softEq(product.productId, addedProduct.productId) &&
  !isEmpty(product.productId) && // productId は必須
  softEq(product.originId, addedProduct.originId) &&
  softEq(product.rankId, addedProduct.rankId) &&
  softEq(product.productClassId, addedProduct.productClassId) &&
  softEq(product.packingId, addedProduct.packingId) &&
  softEq(product.weight, addedProduct.weight) &&
  softEq(product.weightUnitId, addedProduct.weightUnitId);

const CombinedSidebar = ({
  open,
  activeMode,
  tabPosition,
  deliveryDate,
  supplierGroupValue,
  supplyDivisionValue,
  submitVersion,
  addedProductList,
  predictAddedProduct,
  onAddProduct,
  onAddProductMaster,
  onAddProductDetailMaster,
  onDragBegin,
  onDragCancel,
  onClose,
  refreshTrigger,
  scrollToProductId,
  onScrollComplete,
  // History props
  hidden,
  supplyHistoryAll,
  handleBack,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filterClearKey, setFilterClearKey] = useState(0);
  const scrollRef = useRef(null);

  // Master data for autocomplete
  const [products, setProducts] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [productClasses, setProductClasses] = useState([]);
  const [packings, setPackings] = useState([]);

  // Filter states (autocomplete value can be object OR string for free input)
  const [filterProduct, setFilterProduct] = useState(null);
  const [filterOrigin, setFilterOrigin] = useState(null);
  const [filterRank, setFilterRank] = useState(null);
  const [filterProductClass, setFilterProductClass] = useState(null);
  const [filterPacking, setFilterPacking] = useState(null);
  const [weightUnits, setWeightUnits] = useState([]);
  const [filterWeight, setFilterWeight] = useState(""); // free text for 重量
  const [filterWeightUnit, setFilterWeightUnit] = useState(null); // g/kg select
  const [filterOtherSpec, setFilterOtherSpec] = useState(""); // free text for その他規格
  const [filterRemark, setFilterRemark] = useState(""); // free text for 備考

  const predictFn = predictAddedProduct || defaultPredictAdded;
  const activePart = tabPosition ?? 1;

  // Fetch master data for autocomplete dropdowns
  const fetchMasterData = async () => {
    setLoading(true);
    try {
      const [productRes, originRes, rankRes, classRes, packingRes, weightUnitRes] =
        await Promise.all([
          getProduct({}),
          getOrigin(),
          getRank(),
          getProductClass(),
          getPacking(),
          getWeightUnit(),
        ]);

      const productList = (productRes.listProductInfo ?? [])
        .filter((p) => p.productName)
        .sort((a, b) => a.productId - b.productId);
      setProducts(productList);

      const originList = (originRes.masterVarInfo ?? [])
        .map((e) => ({ originId: e.varCd, originName: e.varValue1 }))
        .filter((x) => x.originName?.trim());
      setOrigins(originList);

      const rankList = (rankRes.masterVarInfo ?? [])
        .map((e) => ({ rankId: e.varCd, rankName: e.varValue1 }))
        .filter((x) => x.rankName?.trim());
      setRanks(rankList);

      const classList = (classRes.masterVarInfo ?? [])
        .map((e) => ({
          productClassId: e.varCd,
          productClassName: e.varValue1,
        }))
        .filter((x) => x.productClassName?.trim());
      setProductClasses(classList);

      const packingList = (packingRes.masterVarInfo ?? [])
        .map((e) => ({ packingId: e.varCd, packingName: e.varValue1 }))
        .filter((x) => x.packingName?.trim());
      setPackings(packingList);

      const weightUnitList = (weightUnitRes.masterVarInfo ?? [])
        .map((e) => ({ weightUnitId: e.varCd, weightUnitName: e.varValue1 }))
        .filter((x) => x.weightUnitName?.trim());
      setWeightUnits(weightUnitList);
    } catch (e) {
      console.error("マスタデータ取得エラー:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchMasterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Reset on tab change
  useEffect(() => {
    setSelectedProduct(null);
    setVisibleCount(PAGE_SIZE);
  }, [tabPosition]);

  // Reset pagination when any filter changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [
    filterProduct,
    filterOrigin,
    filterRank,
    filterProductClass,
    filterPacking,
    filterWeight,
    filterOtherSpec,
    filterRemark,
  ]);

  // Infinite scroll
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      setVisibleCount((prev) => prev + PAGE_SIZE);
    }
  }, []);

  // Helpers to extract id/name when value can be object or free string
  const getId = (val, idKey) =>
    val && typeof val === "object" ? val[idKey] : null;
  const getName = (val, nameKey) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    return val[nameKey] || "";
  };

  // Check if any filter is active
  const hasActiveFilter =
    filterProduct ||
    filterOrigin ||
    filterRank ||
    filterProductClass ||
    filterPacking ||
    filterWeight ||
    filterOtherSpec ||
    filterRemark;

  // Clear all filters (force remount Autocompletes via key to clear free input)
  const handleClearFilters = () => {
    setFilterProduct(null);
    setFilterOrigin(null);
    setFilterRank(null);
    setFilterProductClass(null);
    setFilterPacking(null);
    setFilterWeight("");
    setFilterWeightUnit(null);
    setFilterOtherSpec("");
    setFilterRemark("");
    setFilterClearKey((k) => k + 1);
  };

  // 以下のいずれかが発生した場合、検索条件をクリアし商品検索タブを閉じる:
  //   - 発注条件 (発注日・荷主・発注区分) の変更
  //   - 発注確定 (submit) の成功 (submitVersion がインクリメントされる)
  // 初回マウント時は無視する (初期表示で誤クリア・誤クローズしないため)。
  const isClearTriggerInitMount = useRef(true);
  useEffect(() => {
    if (isClearTriggerInitMount.current) {
      isClearTriggerInitMount.current = false;
      return;
    }
    handleClearFilters();
    if (onClose) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate, supplierGroupValue, supplyDivisionValue, submitVersion]);

  // When selecting a row, reflect values into autocomplete filters
  const handleSelect = (data) => {
    setSelectedProduct((prev) => {
      if (
        prev &&
        prev.productId === data.productId &&
        prev.productDetailId === data.productDetailId
      ) {
        return null;
      }

      // Reflect selected product values to filters.
      // マスタに ID が存在しない場合は null にする (string を保持しない)。
      // 値が null でも handleBlur のエラー判定からは外れる (空入力は OK)。
      const matchedProduct =
        products.find((p) => p.productId === data.productId) || null;
      setFilterProduct(matchedProduct);

      const matchedOrigin =
        origins.find((o) => o.originId == data.originId) || null;
      setFilterOrigin(matchedOrigin);

      const matchedRank =
        ranks.find((r) => r.rankId == data.rankId) || null;
      setFilterRank(matchedRank);

      const matchedClass =
        productClasses.find((c) => c.productClassId == data.productClassId) ||
        null;
      setFilterProductClass(matchedClass);

      const matchedPacking =
        packings.find((p) => p.packingId == data.packingId) || null;
      setFilterPacking(matchedPacking);

      // Weight + unit
      setFilterWeight(data.weight != null ? String(data.weight) : "");
      const matchedWeightUnit =
        weightUnits.find((u) => u.weightUnitId == data.weightUnitId) || null;
      setFilterWeightUnit(matchedWeightUnit);

      // その他規格
      const otherSpecVal = data.otherSpec;
      setFilterOtherSpec(otherSpecVal != null ? String(otherSpecVal) : "");

      // 備考
      const remarkVal = data.remark ?? data.remarks;
      setFilterRemark(remarkVal != null ? String(remarkVal) : "");

      return data;
    });
  };

  // Build current spec values for drag-and-drop into supply list
  const buildFilterDragItem = () => {
    const productObj =
      filterProduct && typeof filterProduct === "object" ? filterProduct : null;
    const originId = getId(filterOrigin, "originId");
    const rankId = getId(filterRank, "rankId");
    const productClassId = getId(filterProductClass, "productClassId");
    const packingId = getId(filterPacking, "packingId");

    const originName = getName(filterOrigin, "originName");
    const rankName = getName(filterRank, "rankName");
    const productClassName = getName(filterProductClass, "productClassName");
    const packingName = getName(filterPacking, "packingName");
    const weightVal = filterWeight ? Number(filterWeight) || filterWeight : null;
    const weightUnitId = filterWeightUnit ? Number(filterWeightUnit.weightUnitId) : null;
    const weightUnitName = filterWeightUnit ? filterWeightUnit.weightUnitName : null;
    // 入数 はフリーテキスト ("6玉/箱" など) なので文字列のまま渡す
    const otherSpecStr = filterOtherSpec ? String(filterOtherSpec) : null;
    const remarkStr = filterRemark ? String(filterRemark) : null;

    return {
      type: DND_ITEM_TYPES.ADD_PRODUCT_ROW,
      part: activePart,
      productId: productObj?.productId ?? null,
      productName: productObj?.productName ?? "",
      productDetailId: null,
      originId,
      origin: originName,
      originName,
      rankId,
      rank: rankName,
      rankName,
      productClassId,
      productClass: productClassName,
      productClassName,
      packingId,
      packing: packingName,
      packingName,
      weight: weightVal,
      weightUnitId,
      weightUnit: weightUnitName,
      weightUnitName,
      otherSpec: otherSpecStr,
      remark: remarkStr,
      remarks: remarkStr,
      orderingUnit: productObj?.orderingUnit ?? "",
      orderingUnitId: productObj?.orderingUnitId ?? null,
    };
  };

  // 全 supplyDetails を比較対象にする (タブ絞り込みなし)
  // 履歴側 / フィルタバー側どちらの重複検出にも利用する
  const addedForTab = addedProductList ?? [];

  // Check if filter bar values match an existing supplyDetail (duplicate check for drag)
  const filterBarItem = {
    productId:
      filterProduct && typeof filterProduct === "object"
        ? filterProduct.productId
        : null,
    originId:
      filterOrigin && typeof filterOrigin === "object"
        ? filterOrigin.originId
        : null,
    rankId:
      filterRank && typeof filterRank === "object" ? filterRank.rankId : null,
    productClassId:
      filterProductClass && typeof filterProductClass === "object"
        ? filterProductClass.productClassId
        : null,
    packingId:
      filterPacking && typeof filterPacking === "object"
        ? filterPacking.packingId
        : null,
    weight: filterWeight ? filterWeight : null,
    weightUnitId: filterWeightUnit ? filterWeightUnit.weightUnitId : null,
    otherSpec: filterOtherSpec ? String(filterOtherSpec) : null,
  };

  const filterBarDuplicate =
    !!filterBarItem.productId &&
    addedForTab.some((addedData) => predictFn(filterBarItem, addedData));

  // Drag from filter bar to supply list (disabled when duplicate)
  const filterDragRef = useRef(null);
  const canDragFilter =
    !!(filterProduct && typeof filterProduct === "object") &&
    !filterBarDuplicate;
  const [{ isDraggingFilter }, dragFilter, dragPreviewFilter] = useDrag(
    () => ({
      type: DND_ITEM_TYPES.ADD_PRODUCT_ROW,
      item: () => {
        onDragBegin?.();
        return buildFilterDragItem();
      },
      end: () => {
        onDragCancel?.();
      },
      canDrag: () => canDragFilter,
      collect: (monitor) => ({ isDraggingFilter: monitor.isDragging() }),
    }),
    [
      filterProduct,
      filterOrigin,
      filterRank,
      filterProductClass,
      filterPacking,
      filterWeight,
      filterWeightUnit,
      filterOtherSpec,
      filterRemark,
      activePart,
      canDragFilter,
    ],
  );
  dragFilter(filterDragRef);

  // Plan B: ネイティブのドラッグ画像を抑制し、<FreeProductDragPreview /> でカスタム描画する
  useEffect(() => {
    dragPreviewFilter(getEmptyImage(), { captureDraggingState: true });
  }, [dragPreviewFilter]);

  // 履歴は supplyHistoryAll 全件を使う。発注表に既にある行は added=true で灰色表示。
  // 検索条件にヒットする履歴のみ表示する。条件がない場合は全履歴を表示。
  const matchHistoryFilter = (data) => {
    if (
      filterProduct &&
      typeof filterProduct === "object" &&
      data.productId !== filterProduct.productId
    )
      return false;
    if (filterOrigin) {
      if (typeof filterOrigin === "object") {
        if (data.originId != filterOrigin.originId) return false;
      } else {
        const s = String(filterOrigin).toLowerCase();
        if (!(data.origin || data.originName || "").toLowerCase().includes(s))
          return false;
      }
    }
    if (filterRank) {
      if (typeof filterRank === "object") {
        if (data.rankId != filterRank.rankId) return false;
      } else {
        const s = String(filterRank).toLowerCase();
        if (!(data.rank || data.rankName || "").toLowerCase().includes(s))
          return false;
      }
    }
    if (filterProductClass) {
      if (typeof filterProductClass === "object") {
        if (data.productClassId != filterProductClass.productClassId)
          return false;
      } else {
        const s = String(filterProductClass).toLowerCase();
        if (
          !(data.productClass || data.productClassName || "")
            .toLowerCase()
            .includes(s)
        )
          return false;
      }
    }
    if (filterPacking) {
      if (typeof filterPacking === "object") {
        if (data.packingId != filterPacking.packingId) return false;
      } else {
        const s = String(filterPacking).toLowerCase();
        if (
          !(data.packing || data.packingName || "").toLowerCase().includes(s)
        )
          return false;
      }
    }
    if (
      filterWeight &&
      !String(data.weight ?? "").includes(String(filterWeight).trim())
    )
      return false;
    if (
      filterOtherSpec &&
      !String(data.otherSpec ?? "").includes(String(filterOtherSpec).trim())
    )
      return false;
    if (filterRemark) {
      const remarkVal = data.remark ?? data.remarks ?? "";
      if (!String(remarkVal).includes(String(filterRemark).trim())) return false;
    }
    return true;
  };

  const historySource =
    supplyHistoryAll && supplyHistoryAll.length > 0 ? supplyHistoryAll : hidden;

  const historyItems = (historySource ?? [])
    .filter((data) => data.productName && matchHistoryFilter(data))
    .map((data) => ({
      ...data,
      displaySize:
        data.displaySize ||
        getDisplaySize(
          data.rank ?? data.rankName,
          data.productClass ?? data.productClassName,
          data.weight,
          data.weightUnit ?? data.weightUnitName,
          data.packing ?? data.packingName,
          data.otherSpec,
        ),
      added: addedForTab.some((addedData) => predictFn(data, addedData)),
      isHistory: true,
    }));

  // 未追加(黄色)を上に、追加済み(灰色)を下に並べる
  const combinedList = [
    ...historyItems.filter((d) => !d.added),
    ...historyItems.filter((d) => d.added),
  ];
  const hasHistoryResults = historyItems.length > 0;

  // Render list (history only)
  const renderList = () => {
    const visibleItems = combinedList.slice(0, visibleCount);
    return visibleItems.map((item, i) => (
      <CombinedSidebarRow
        key={`${item.productId}-${item._groupKey || ""}-h-${i}`}
        data={item}
        part={activePart}
        isHistory={true}
        onSelect={handleSelect}
        onDragBegin={onDragBegin}
        onDragCancel={onDragCancel}
        onRestore={(product) => handleBack?.(product)}
      />
    ));
  };

  return (
    <Box
      sx={{
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Custom drag preview (overlay following cursor) */}
      <FreeProductDragPreview />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            商品検索
          </Typography>
          {hasHistoryResults && (
            <Chip
              label={`${historyItems.length}件`}
              size="small"
              sx={{
                fontSize: 13,
                fontWeight: 500,
                bgcolor: "warning.light",
                color: "warning.contrastText",
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
            onClick={onClose}
            sx={{
              fontSize: 12,
              py: 0.5,
              px: 1.5,
              minWidth: "auto",
              color: "grey.600",
              borderColor: "grey.400",
              "&:hover": {
                color: "error.main",
                borderColor: "error.main",
                bgcolor: "error.50",
              },
            }}
          >
            閉じる
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Master dialog button */}
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={() => onAddProductMaster?.()}
          sx={{ fontSize: 14, flex: 1 }}
        >
          商品追加
        </Button>
      </Box>

      {/* Autocomplete filter fields */}
      <Box
        sx={{
          mb: 1,
          p: 1.5,
          border: "2px solid",
          borderColor: canDragFilter ? "primary.main" : "grey.300",
          borderRadius: 2,
          bgcolor: filterBarDuplicate ? "warning.50" : "grey.50",
          transition: "border-color 0.2s ease, background-color 0.2s ease",
        }}
      >
        {/* Top bar: 全項目クリア ボタン (品名列に揃える) + 発注表に追加済です ラベル (右) */}
        {/* pl: FreeProduct の drag handle (28px) + gap (4px) ぶん右へ寄せる */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
            pl: "32px",
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<ClearIcon sx={{ fontSize: 18 }} />}
            onClick={handleClearFilters}
            sx={{
              fontSize: 15,
              fontWeight: 700,
              py: 0.5,
              px: 1.5,
              whiteSpace: "nowrap",
              color: "text.primary",
              borderColor: "grey.500",
              borderWidth: 2,
              bgcolor: "#ffffff",
              "&:hover": {
                borderWidth: 2,
                borderColor: "error.main",
                color: "error.main",
                bgcolor: "error.50",
              },
            }}
          >
            全項目クリア
          </Button>
          {filterBarDuplicate && (
            <Box
              sx={{
                px: 1,
                py: 0.5,
                bgcolor: "warning.light",
                color: "warning.contrastText",
                borderRadius: 1,
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              発注表に追加済です。
            </Box>
          )}
        </Box>

        <FreeProduct
          filterClearKey={filterClearKey}
          filterProduct={filterProduct}
          setFilterProduct={setFilterProduct}
          filterOrigin={filterOrigin}
          setFilterOrigin={setFilterOrigin}
          filterRank={filterRank}
          setFilterRank={setFilterRank}
          filterProductClass={filterProductClass}
          setFilterProductClass={setFilterProductClass}
          filterPacking={filterPacking}
          setFilterPacking={setFilterPacking}
          filterWeight={filterWeight}
          setFilterWeight={setFilterWeight}
          filterWeightUnit={filterWeightUnit}
          setFilterWeightUnit={setFilterWeightUnit}
          filterOtherSpec={filterOtherSpec}
          setFilterOtherSpec={setFilterOtherSpec}
          filterRemark={filterRemark}
          setFilterRemark={setFilterRemark}
          products={products}
          origins={origins}
          ranks={ranks}
          productClasses={productClasses}
          packings={packings}
          weightUnits={weightUnits}
          filterDragRef={filterDragRef}
          canDragFilter={canDragFilter}
          isDraggingFilter={isDraggingFilter}
        />

      </Box>

      {/* Column header */}
      <CombinedSidebarHeader />

      {/* Result count */}
      <Typography
        sx={{ fontSize: 13, color: "text.secondary", mb: 0.5, px: 0.5 }}
      >
        {combinedList.length}件表示
        {hasActiveFilter ? "（絞り込み中）" : ""}
      </Typography>

      {/* Scrollable list */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}
      >
        {loading && combinedList.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : combinedList.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, fontSize: "0.8rem" }}>
            発注履歴に該当する商品がありません
          </Typography>
        ) : (
          <>
            {renderList()}
            {visibleCount < combinedList.length && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CombinedSidebar;
